import axios from 'axios';
import { useRouter } from 'next/router';
import qs from 'qs';
import { useCallback } from 'react';
import { Image } from '@components/common/Image';
import { UiKitButton } from '@components/common/ui-kit-button';
import { currencyReplacer } from '@features/Payments';
import { ButtonGroup } from '@features/Unsubscribe/components/ButtonGroup';
import { Container } from '@features/Unsubscribe/components/Container';
import { Title } from '@features/Unsubscribe/components/Title';
import { useTransferToLifetime } from '@features/Unsubscribe/hooks/use-transfer-to-lifetime';
import { useUnsubscribeAnalytics } from '@features/Unsubscribe/hooks/use-unsubscribe-analytics';
import type { TpWizardSteps } from '@features/Unsubscribe/types';
import { useGetAppName } from 'src/utils/hooks';
import { WIZARD_STEPS } from '../../сonstants';
import { UiKitLoadingButton } from '@/components/common/ui-kit-loading-button';
import styles from './LifetimeAccess.module.scss';
export const LifetimeAccess = (props: TpWizardSteps) => {
  const {
    defaultEventOptions,
    trackUnsubscribeEvent,
    trackUnsubscribeRequestError,
    trackUnsubscribeRequestSuccess
  } = useUnsubscribeAnalytics();
  const {
    wizard,
    allUnsubscribeData
  } = props;
  const {
    query
  } = useRouter();
  const app = useGetAppName();
  const lifetimePlan = allUnsubscribeData?.cancellation_endless_discount;
  const currency = currencyReplacer(lifetimePlan ? lifetimePlan.currency : 'USD');
  const {
    mutate: handleNext,
    isPending: isLoading
  } = useTransferToLifetime({
    query: query,
    wizard: wizard,
    eventName: 'cancel_lifetime_access_chosen',
    options: {
      variant: '1-get lifetime access'
    },
    successStep: WIZARD_STEPS.LIFETIME_ACTIVATED,
    price_id: lifetimePlan?.price_id,
    currency: lifetimePlan?.currency
  });
  const handleCancel = useCallback(async () => {
    const formData = {
      app: app,
      email: defaultEventOptions.email,
      user_id: defaultEventOptions.userId
    };
    const requestOptions = {
      endpoint: 'cancel_subscription',
      requestName: 'cancel_subscription',
      source: 'lifetime_access_cancel_anyway'
    };
    try {
      const response = await axios({
        method: 'post',
        url: `${process.env.NEXT_PUBLIC_API_MICROSERVICE_ENDPOINT}cancel_subscription`,
        data: qs.stringify(formData)
      });
      trackUnsubscribeRequestSuccess('cancel_subscription_cancel_succeeded', requestOptions, response.status);
      trackUnsubscribeEvent('cancel_lifetime_access_chosen', {
        variant: '2-cancel'
      });
      wizard.goToStep(WIZARD_STEPS.SAD);
    } catch (error) {
      trackUnsubscribeRequestError('cancel_subscription_cancel_failed', error, requestOptions);
      wizard.goToStep(WIZARD_STEPS.ERROR);
      console.log('error', error);
    }
  }, [defaultEventOptions.email, defaultEventOptions.userId, trackUnsubscribeEvent, trackUnsubscribeRequestError, trackUnsubscribeRequestSuccess, wizard, app]);
  return <Container className={styles.container} eventOnMounted="cancel_lifetime_access_opened" eventOptions={defaultEventOptions} data-sentry-element="Container" data-sentry-component="LifetimeAccess" data-sentry-source-file="index.tsx">
      <Title className={styles.title} data-sentry-element="Title" data-sentry-source-file="index.tsx">Get lifetime access</Title>
      <p className={styles.subtitle}>
        One-off purchase to use all <br />
        the Premium features forever
      </p>
      <Image src="lifetime-access.webp" alt="lifetime-access" width={327} height={210} data-sentry-element="Image" data-sentry-source-file="index.tsx" />
      <div className={styles.card}>
        <div className={styles.leftSide}>
          <b>Lifetime access</b>
          <p className={styles.cardSubtitle}>One-off payment</p>
        </div>
        <div className={styles.right}>
          <div className={styles.rightSide}>
            <b className={styles.discount}>
              -{lifetimePlan?.discountPercentage}%
            </b>
            <b>
              {currency}
              {lifetimePlan?.fullPriceDiscount}
            </b>
          </div>

          <p className={styles.cardSubtitle}>
            <s>
              {currency}
              {lifetimePlan?.fullPrice}
            </s>
          </p>
        </div>
      </div>

      <ButtonGroup className={styles.buttonGroup} data-sentry-element="ButtonGroup" data-sentry-source-file="index.tsx">
        <UiKitButton color="pure" onClick={handleCancel} className={styles.button} data-sentry-element="UiKitButton" data-sentry-source-file="index.tsx">
          Cancel anyway
        </UiKitButton>
        <UiKitLoadingButton fullWidth onClick={() => handleNext()} loading={isLoading} data-sentry-element="UiKitLoadingButton" data-sentry-source-file="index.tsx">
          Get lifetime access
        </UiKitLoadingButton>
      </ButtonGroup>
    </Container>;
};